import {
    Component,
    Input,
    Inject,
    AfterViewInit,
    OnDestroy
} from 'ng-metadata/core';
import {ChangeLangService} from "../../infra/lang/changeLang.service";
import {MediaQueries} from "../../infra/mediaQueries.service";


@Component({
    selector: 'prm-info-popup',
    templateUrl: 'components/search/infoPopup/infoPopup.html',
})
export class InfoPopup implements AfterViewInit, OnDestroy {
    @Input() content: string;
    @Input() dataQaLabel = "info_popup_btn";

    private popupEl: HTMLElement; // The actual popup element
    private triggerEl: HTMLElement; // Our trigger - the button with the info icon

    private triggerClicked = false;
    private popupId: string; // Holds a dynamic value for the popup id, so we can use it with aria-describedby
    private hideTimeout: any;

    constructor(@Inject('$element') private $element: any,
                @Inject('$scope') private $scope: angular.IScope,
                private changeLangService: ChangeLangService,
                private mediaQueries: MediaQueries) {}

    ngAfterViewInit(): void {
        this.popupId = `info-popup-msg-${this.$scope.$id}`;
        const host = this.$element[0];
        this.popupEl = host.querySelector('.info-popup-msg') as HTMLElement | null;
        this.triggerEl = host.querySelector('.info-popup-icon-wrapper') as HTMLElement | null;

        if (!this.popupEl || !this.triggerEl) {
            console.error('Required DOM elements for InfoPopup were not found.');
            return;
        }

        // Move to <body> and hide initially
        document.body.appendChild(this.popupEl);
        this.popupEl.style.display = 'none';

        // Add hover listeners
        this.triggerEl.addEventListener('mouseenter', this.onTriggerEnter);
        this.triggerEl.addEventListener('mouseleave', this.onTriggerLeave);
        this.popupEl.addEventListener('mouseenter', this.onPopupEnter);
        this.popupEl.addEventListener('mouseleave', this.onPopupLeave);

        // Focus tabbing and closing
        this.popupEl.addEventListener('keydown', this.onPopupKeyDown);
        this.triggerEl.addEventListener('keydown', this.onTriggerKeyDown);

        this.triggerEl.addEventListener('click', this.onTriggerClick);
        this.popupEl.addEventListener('click', this.onPopupClick);
    }

    private onTriggerEnter = () => {
        this.clearHideTimeout();
        this.updateVisibility();
    };

    private onTriggerLeave = () => {
        this.scheduleHide();
    };

    private onPopupEnter = () => {
        this.clearHideTimeout();
        this.updateVisibility();
    };

    private onPopupLeave = () => {
        this.scheduleHide();
    };


    public show() {
        this.clearHideTimeout();
        this.updateVisibility();
    }

    public hide() {
        this.scheduleHide();
    }

    private scheduleHide() {
        this.hideTimeout = setTimeout(() => {
            const activeEl = document.activeElement;
            const focusInsidePopup =
                this.popupEl.contains(activeEl) || this.triggerEl.contains(activeEl);

            if (!focusInsidePopup) {
                this.popupEl.style.display = 'none';
            }
        }, 200);
    }

    private clearHideTimeout() {
        if (this.hideTimeout) {
            clearTimeout(this.hideTimeout);
            this.hideTimeout = null;
        }
    }

    private updateVisibility() {
        this.popupEl.style.display = 'block';
        this.setPosition();
    }

    private setPosition() {
        const rect = this.triggerEl.getBoundingClientRect();
        const popupWidth = this.popupEl.offsetWidth || 200;
        const viewportWidth = window.innerWidth;
        const scrollX = window.scrollX;
        const scrollY = window.scrollY;

        let left: number;

        if (this.isRTL()) {
            // Default RTL: align right edge of trigger with right edge of popup
            left = rect.right - popupWidth + scrollX;

            // If popup overflows left side, flip to appear from left of trigger
            if (left < scrollX + 5) {
                left = rect.left + scrollX;
            }
        } else {
            // Default LTR: align left edge of popup with left edge of trigger
            left = rect.left + scrollX;

            // If popup overflows right edge, flip to align right edges
            if (left + popupWidth > viewportWidth + scrollX - 5) {
                left = rect.right - popupWidth + scrollX;
            }
        }

        Object.assign(this.popupEl.style, {
            position: 'absolute',
            top: `${rect.bottom + scrollY}px`,
            left: `${left}px`,
            zIndex: '9999',
        });
    }

    // Handling Tab movement inside the popup + Escape closing
    private onPopupKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Tab') {
            const focusableEls = this.getFocusableElements(this.popupEl);
            const firstEl = focusableEls[0];
            const lastEl = focusableEls[focusableEls.length - 1];

            if (event.shiftKey) {
                // Shift + Tab on first item will go back to icon
                if (document.activeElement === firstEl) {
                    event.preventDefault();
                    const button = this.triggerEl.querySelector('button');
                    if (button) {
                        button.focus();
                    }
                }
            } else {
                // Tab on last item will close popup and move forward
                if (document.activeElement === lastEl) {
                    event.preventDefault();
                    this.hide();
                    // move focus to next focusable element after icon
                    this.focusNextElementAfterTrigger();
                }
            }
        } else if (event.key === 'Escape') {
            const trigButton = this.triggerEl.querySelector('button');
            if (trigButton) {
                trigButton.focus();
            }
            event.preventDefault();
            this.popupEl.style.display = 'none';
            this.clearHideTimeout();
        }
    };

    private getFocusableElements(container: HTMLElement): HTMLElement[] {
        return Array.from(container.querySelectorAll<HTMLElement>(
            'a[href], button, input, textarea, select, [tabindex]:not([tabindex="-1"])'
        )).filter(el => !el.hasAttribute('disabled'));
    }

    private focusNextElementAfterTrigger() {
        if (!this.triggerEl) return;

        const button = this.triggerEl.querySelector('button');
        if (!button) return;

        const focusables = this.getFocusableElements(document.body);
        const currentIndex = focusables.indexOf(button);
        if (currentIndex >= 0 && currentIndex + 1 < focusables.length) {
            focusables[currentIndex + 1].focus();
        }
    }

    private onTriggerKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Tab' && !event.shiftKey) {
            // Only try to move focus to the popup if popup is visible
            if (this.popupEl.style.display === 'block') {
                const focusableEls = this.getFocusableElements(this.popupEl);
                if (focusableEls.length > 0) {
                    event.preventDefault();
                    focusableEls[0].focus();
                }
            } //else - regular tab movement pass the popup

        } else if (event.key === 'Escape') {
            event.preventDefault();
            this.popupEl.style.display = 'none';
            this.clearHideTimeout();
        }
    };

    private onTriggerClick = (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();

        if (this.triggerClicked) {
            this.popupEl.style.display = 'none';
            this.triggerClicked=false;
            this.clearHideTimeout();
        } else {
            this.triggerClicked=true;
            this.show();
        }
    };

    private isRTL () {
        return this.changeLangService.direction === 'rtl';
    }

    public isMobile(){
        return this.mediaQueries.xs || this.mediaQueries.sm;
    }

    private onPopupClick = (event: MouseEvent) => {
        const target = event.target as HTMLElement;

        // If a link is clicked, close the popup
        if (target && target.closest('a[href]')) {
            this.popupEl.style.display = 'none';
            this.triggerClicked = false;
            this.clearHideTimeout();
        }
    };


    ngOnDestroy(): void {
        this.popupEl.remove();
        this.triggerEl.removeEventListener('mouseenter', this.onTriggerEnter);
        this.triggerEl.removeEventListener('mouseleave', this.onTriggerLeave);
        this.popupEl.removeEventListener('mouseenter', this.onPopupEnter);
        this.popupEl.removeEventListener('mouseleave', this.onPopupLeave);
        this.popupEl.removeEventListener('keydown', this.onPopupKeyDown);
        this.triggerEl.removeEventListener('keydown', this.onTriggerKeyDown);
        this.triggerEl.removeEventListener('click', this.onTriggerClick);
        this.popupEl.removeEventListener('click', this.onPopupClick);
    }
}



// WEBPACK FOOTER //
// ./src/main/webapp/components/search/infoPopup/infoPopup.directive.ts